import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import DogEditModal from './DogEditModal';
import './WallReports.css';

function WallReports({ currentUser }) {
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genType, setGenType] = useState('one_off');
  const [genFrom, setGenFrom] = useState('');
  const [genTo, setGenTo] = useState('');
  const [exportingWall, setExportingWall] = useState(false);
  const [actioningDog, setActioningDog] = useState(null);
  const [selectedDogName, setSelectedDogName] = useState(null);
  const [sortColumn, setSortColumn] = useState('crossed_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterDog, setFilterDog] = useState('');
  const [filterTrack, setFilterTrack] = useState('');
  const [filterTrainer, setFilterTrainer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOnWall, setFilterOnWall] = useState('');
  const [filterAction, setFilterAction] = useState('actionable'); // default per spec

  const fetchReports = async (selectId) => {
    try {
      setLoadingReports(true);
      const response = await api.get('/api/wall-reports');
      const list = response.data.reports || [];
      setReports(list);
      const target = selectId || (list.length > 0 ? list[0].id : null);
      if (target) {
        setSelectedReportId(target);
        fetchDetail(target);
      } else {
        setSelectedReportId(null);
        setDetail(null);
      }
    } catch (error) {
      console.error('Error fetching wall reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchDetail = async (reportId) => {
    try {
      setLoadingDetail(true);
      const response = await api.get(`/api/wall-reports/${reportId}`);
      setDetail(response.data);
    } catch (error) {
      console.error('Error fetching wall report detail:', error);
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async () => {
    const label = genType === 'weekly'
      ? 'weekly report (dogs newly over the threshold since the last weekly report)'
      : 'one-off report (every threshold crossing since launch)';
    if (!window.confirm(`Generate a ${label} now?`)) return;
    try {
      setGenerating(true);
      const body = { report_type: genType };
      if (genFrom) body.window_from = genFrom;
      if (genTo) body.window_to = genTo;
      const response = await api.post('/api/wall-reports/generate', body);
      const newId = response.data?.report?.id;
      await fetchReports(newId);
    } catch (error) {
      console.error('Error generating wall report:', error);
      alert(error.response?.data?.error || 'Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const downloadBlob = (data, fallbackName, contentDisposition) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/"/g, '')
      : fallbackName;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportReport = async (report) => {
    try {
      const response = await api.get(`/api/wall-reports/${report.id}/export`, {
        responseType: 'blob'
      });
      downloadBlob(
        response.data,
        `wall_report_${report.id}.csv`,
        response.headers['content-disposition']
      );
    } catch (error) {
      console.error('Error exporting wall report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const handleExportCurrentWall = async () => {
    try {
      setExportingWall(true);
      const response = await api.get('/api/missing-dogs/export', { responseType: 'blob' });
      downloadBlob(
        response.data,
        `missing_wall_${new Date().toISOString().slice(0, 10)}.csv`,
        response.headers['content-disposition']
      );
    } catch (error) {
      console.error('Error exporting missing wall:', error);
      alert('Failed to export missing wall. Please try again.');
    } finally {
      setExportingWall(false);
    }
  };

  const handleAddToWall = async (row) => {
    const ok = window.confirm(
      `Add "${row.dog_name}" to the public missing wall?`
    );
    if (!ok) return;
    try {
      setActioningDog(row.dog_name);
      await api.put(`/api/dogs/${encodeURIComponent(row.dog_name)}/metadata`, {
        is_missing: true
      });
      if (selectedReportId) await fetchDetail(selectedReportId);
    } catch (error) {
      console.error('Error adding dog to wall:', error);
      alert(error.response?.data?.error || 'Failed to add dog to the wall. Please try again.');
    } finally {
      setActioningDog(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateStr; }
  };

  const typeLabel = (type) => (type === 'weekly' ? 'Weekly' : 'One-off');

  const handleHeaderClick = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortArrow = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const sortableHeaderProps = (column) => ({
    className: 'sortable',
    tabIndex: 0,
    'aria-sort': sortColumn === column ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none',
    onClick: () => handleHeaderClick(column),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleHeaderClick(column);
      }
    },
  });

  const isAnyFilterActive = (
    filterDog.trim() !== '' ||
    filterTrack.trim() !== '' ||
    filterTrainer.trim() !== '' ||
    filterStatus.trim() !== '' ||
    filterOnWall !== '' ||
    filterAction !== 'actionable'
  );

  const NUMERIC_COLUMNS = ['days_unseen', 'current_is_missing', 'actionable', 'crossings_in_window'];

  const displayedRows = useMemo(() => {
    const rows = detail?.rows || [];
    const dogQ = filterDog.trim().toLowerCase();
    const trackQ = filterTrack.trim().toLowerCase();
    const trainerQ = filterTrainer.trim().toLowerCase();
    const statusQ = filterStatus.trim().toLowerCase();

    const filtered = rows.filter((r) => {
      if (dogQ && !(r.dog_name || '').toLowerCase().includes(dogQ)) return false;
      if (trackQ && !(r.track || '').toLowerCase().includes(trackQ)) return false;
      if (trainerQ && !(r.trainer || '').toLowerCase().includes(trainerQ)) return false;
      if (statusQ) {
        const status = (r.current_status || r.status_at_generation || '').toLowerCase();
        if (!status.includes(statusQ)) return false;
      }
      if (filterOnWall === 'yes' && !r.current_is_missing) return false;
      if (filterOnWall === 'no' && r.current_is_missing) return false;
      if (filterAction === 'actionable' && !r.actionable) return false;
      if (filterAction === 'info' && r.actionable) return false;
      return true;
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    const value = (r) => {
      if (sortColumn === 'status') return r.current_status || r.status_at_generation || '';
      return r[sortColumn];
    };
    filtered.sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (NUMERIC_COLUMNS.includes(sortColumn)) {
        return ((av || 0) - (bv || 0)) * dir;
      }
      const as = String(av == null ? '' : av).toLowerCase();
      const bs = String(bv == null ? '' : bv).toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, sortColumn, sortDirection, filterDog, filterTrack, filterTrainer, filterStatus, filterOnWall, filterAction]);

  const selectedReport = detail?.report;

  return (
    <div className="wall-reports">
      <div className="wall-reports-header">
        <div>
          <h2>Wall Reports</h2>
          <p>
            Dogs that crossed the 30-day missing threshold, per report window.
            Review each report and add missing dogs to the public wall.
          </p>
        </div>
        <button
          type="button"
          className="wall-btn wall-btn-secondary"
          onClick={handleExportCurrentWall}
          disabled={exportingWall}
        >
          {exportingWall ? 'Exporting…' : 'Download Current Missing Wall CSV'}
        </button>
      </div>

      <div className="wall-generate-bar">
        <label>Generate:</label>
        <select value={genType} onChange={(e) => setGenType(e.target.value)} disabled={generating}>
          <option value="one_off">One-off (since launch)</option>
          <option value="weekly">Weekly (since last weekly report)</option>
        </select>
        <label>From:</label>
        <input type="date" value={genFrom} onChange={(e) => setGenFrom(e.target.value)} disabled={generating} title="Leave blank for the default window" />
        <label>To:</label>
        <input type="date" value={genTo} onChange={(e) => setGenTo(e.target.value)} disabled={generating} title="Leave blank for today" />
        <button type="button" className="wall-btn wall-btn-primary" onClick={handleGenerate} disabled={generating}>
          {generating ? 'Generating…' : 'Generate Report'}
        </button>
        <span className="wall-generate-hint">Leave dates blank for the defaults.</span>
      </div>

      {loadingReports ? (
        <div className="wall-loading">Loading reports...</div>
      ) : reports.length === 0 ? (
        <div className="wall-empty">No reports yet. Generate the first one above.</div>
      ) : (
        <div className="wall-table-wrapper">
          <table className="wall-table wall-report-list">
            <thead>
              <tr>
                <th>Generated</th>
                <th>Type</th>
                <th>Window</th>
                <th>Rows</th>
                <th>Actionable</th>
                <th>Generated By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr
                  key={report.id}
                  className={`wall-report-row ${report.id === selectedReportId ? 'selected' : ''}`}
                  onClick={() => { setSelectedReportId(report.id); fetchDetail(report.id); }}
                >
                  <td>{formatDateTime(report.generated_at)}</td>
                  <td>{typeLabel(report.report_type)}</td>
                  <td className="wall-window-cell">{report.window_from} to {report.window_to}</td>
                  <td>{report.row_count}</td>
                  <td>{report.actionable_count}</td>
                  <td>{report.generated_by_username || 'Scheduled'}</td>
                  <td>
                    <button
                      type="button"
                      className="wall-btn wall-btn-small"
                      onClick={(e) => { e.stopPropagation(); handleExportReport(report); }}
                    >
                      Download CSV
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedReport && (
        <div className="wall-detail">
          <h3>
            {typeLabel(selectedReport.report_type)} report, {selectedReport.window_from} to {selectedReport.window_to}
            <span className="wall-detail-meta">
              {selectedReport.row_count} dogs, {selectedReport.actionable_count} actionable
            </span>
          </h3>
          {loadingDetail ? (
            <div className="wall-loading">Loading report rows...</div>
          ) : (detail?.rows || []).length === 0 ? (
            <div className="wall-empty">No dogs crossed the threshold in this window.</div>
          ) : (
            <div className="wall-table-wrapper">
              <table className="wall-table">
                <thead>
                  <tr>
                    <th {...sortableHeaderProps('dog_name')}>Dog{sortArrow('dog_name')}</th>
                    <th {...sortableHeaderProps('crossed_at')}>Crossed On{sortArrow('crossed_at')}</th>
                    <th {...sortableHeaderProps('last_race_date')}>Last Race{sortArrow('last_race_date')}</th>
                    <th {...sortableHeaderProps('days_unseen')}>Days Unseen{sortArrow('days_unseen')}</th>
                    <th {...sortableHeaderProps('track')}>Track{sortArrow('track')}</th>
                    <th {...sortableHeaderProps('trainer')}>Trainer{sortArrow('trainer')}</th>
                    <th {...sortableHeaderProps('status')}>Status{sortArrow('status')}</th>
                    <th {...sortableHeaderProps('current_is_missing')}>On Wall Now{sortArrow('current_is_missing')}</th>
                    <th {...sortableHeaderProps('returned_date')}>Returned{sortArrow('returned_date')}</th>
                    <th {...sortableHeaderProps('actionable')}>Action Needed{sortArrow('actionable')}</th>
                    <th>Actions</th>
                  </tr>
                  <tr className="filter-row">
                    <th>
                      <input type="text" className="filter-input" placeholder="Filter…" value={filterDog} onChange={(e) => setFilterDog(e.target.value)} />
                    </th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th>
                      <input type="text" className="filter-input" placeholder="Filter…" value={filterTrack} onChange={(e) => setFilterTrack(e.target.value)} />
                    </th>
                    <th>
                      <input type="text" className="filter-input" placeholder="Filter…" value={filterTrainer} onChange={(e) => setFilterTrainer(e.target.value)} />
                    </th>
                    <th>
                      <input type="text" className="filter-input" placeholder="Filter…" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} />
                    </th>
                    <th>
                      <select className="filter-input" value={filterOnWall} onChange={(e) => setFilterOnWall(e.target.value)}>
                        <option value="">All</option>
                        <option value="yes">On wall</option>
                        <option value="no">Not on wall</option>
                      </select>
                    </th>
                    <th></th>
                    <th>
                      <select className="filter-input" value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
                        <option value="actionable">Actionable only</option>
                        <option value="info">Informational only</option>
                        <option value="">All</option>
                      </select>
                    </th>
                    <th>
                      {isAnyFilterActive && (
                        <button
                          type="button"
                          className="filter-clear-link"
                          onClick={() => {
                            setFilterDog(''); setFilterTrack(''); setFilterTrainer('');
                            setFilterStatus(''); setFilterOnWall(''); setFilterAction('actionable');
                          }}
                        >
                          Clear filters
                        </button>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="no-match-row">
                        No dogs match the current filters.
                      </td>
                    </tr>
                  ) : displayedRows.map((row) => (
                    <tr key={`${detail.report.id}-${row.dog_name}`} className="wall-dog-row">
                      <td
                        className="dog-name-cell dog-name-cell-clickable"
                        title="Open dog record"
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedDogName(row.dog_name)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedDogName(row.dog_name);
                          }
                        }}
                      >
                        {row.dog_name}
                        {row.crossings_in_window > 1 && (
                          <div className="wall-crossings-note">crossed {row.crossings_in_window}x in window</div>
                        )}
                      </td>
                      <td>{row.crossed_at || ''}</td>
                      <td>{row.last_race_date || ''}</td>
                      <td>{row.days_unseen != null ? row.days_unseen : ''}</td>
                      <td>{row.track || ''}</td>
                      <td>{row.trainer || ''}</td>
                      <td>{row.current_status || row.status_at_generation || ''}</td>
                      <td>
                        <span className={`wall-badge ${row.current_is_missing ? 'wall-badge-yes' : 'wall-badge-muted'}`}>
                          {row.current_is_missing ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{row.returned_to_racing ? `Yes (${row.returned_date || 'unknown'})` : 'No'}</td>
                      <td>
                        <span
                          className={`wall-badge ${row.actionable ? 'wall-badge-action' : 'wall-badge-muted'}`}
                          title={row.actionable ? 'Needs review: not on the wall and still unaccounted for' : `No action needed: ${(row.not_actionable_reason || '').replace(/_/g, ' ')}`}
                        >
                          {row.actionable ? 'Yes' : (row.not_actionable_reason || 'no').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="actions-cell">
                        {Boolean(row.actionable) && !row.current_is_missing && (
                          <button
                            type="button"
                            className="wall-btn wall-btn-small wall-btn-primary"
                            onClick={() => handleAddToWall(row)}
                            disabled={actioningDog === row.dog_name}
                          >
                            {actioningDog === row.dog_name ? 'Adding…' : 'Add to Wall'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {selectedDogName && (
        <DogEditModal
          dogName={selectedDogName}
          currentUser={currentUser}
          onClose={() => setSelectedDogName(null)}
          onSaved={() => { if (selectedReportId) fetchDetail(selectedReportId); }}
        />
      )}
    </div>
  );
}

export default WallReports;
